import type { Dispatch } from 'react';

import type { GlobalState, BrukerState } from './initialState';
import { type InnloggetDataResponse, ActionTypes } from '../types/entities';
import type { HentAntallUlesteResponse, HentKunAntallUlesteHendelserResponse } from '../types/entities/hendelsesmeny.v2entities';

import { fallbackHeaderFooter } from '@helsenorge/core-cms/header-skeleton/data';
import type { HeaderFooterDTO } from '@helsenorge/core-cms/types/entities/cms.v1entities';
import type {
  GetAuthenticatedTilesResponse,
  GetPublicTilesResponse,
} from '@helsenorge/core-cms/types/entities/helsenorge.corefrontend.internalapi.entities.entities';
import LanguageLocales from '@helsenorge/core-utils/constants/languages';
import { merge } from '@helsenorge/core-utils/object-utils';
import { get as ContentAPIGet } from '@helsenorge/framework-utils/cms-content-api-service';
import { hasCookie, hasLoggedInCookie } from '@helsenorge/framework-utils/cookie';
import { isAuthorized as isAuthorizedFromCoreUtils } from '@helsenorge/framework-utils/hn-authorize';
import { isDebug } from '@helsenorge/framework-utils/hn-debug';
import { get as getProxy, erTjenester, getTjenesterUrl, get } from '@helsenorge/framework-utils/hn-proxy-service';
import { get as getTjeneste } from '@helsenorge/framework-utils/hn-tjeneste-service';
import {
  getHarRepresentasjon,
  getInnloggetFulltNavn,
  getSistPalogget,
  getErRepresentasjon,
  getErMellom12Og16,
  getRepresentertBruker,
  getVisPersonvelger,
  getScopes,
  getPersonverninnstillinger,
} from '@helsenorge/framework-utils/hn-user';
import { error as logError, warn } from '@helsenorge/framework-utils/logger';
import type { OperationResponse } from '@helsenorge/framework-utils/types/entities';
import { HNeventSetFooterData } from '@helsenorge/framework-utils/web-component/events';

export interface DispatchAction {
  type: ActionTypes;
  payload: Partial<GlobalState>;
}

export const getUser = (dispatch: Dispatch<DispatchAction>, errorText: string): void => {
  try {
    const bruker: BrukerState = {
      visPersonvelger: getVisPersonvelger(),
      navn: getInnloggetFulltNavn(),
      sistInnlogget: getSistPalogget(),
      harRepresentasjon: getHarRepresentasjon(),
      representertBruker: getRepresentertBruker(),
      erRepresentasjon: getErRepresentasjon(),
      erMellom12Og16: getErMellom12Og16(),
      harBarnMedAnnenAdresse: false,
      harBarnUnder16UtenForeldreansvar: false,
      harBarnOver11: false,
      harBarnOver12: false,
      harBarnOver16: false,
      scopes: getScopes(),
      personverninnstillinger: getPersonverninnstillinger(),
    };

    dispatch({
      type: ActionTypes.GetUserAction,
      payload: { bruker },
    });
  } catch (e) {
    const payload: Partial<GlobalState> = {
      error: { profileError: errorText },
      loading: { global: false },
    };
    dispatch({ type: ActionTypes.error, payload });
    logError('Feil ved henting av brukerdata', e);
  }
};

export const erInnloggetOgErTjenester = (dispatch: Dispatch<DispatchAction>): void => {
  const isAuthorized = hasLoggedInCookie() || isAuthorizedFromCoreUtils();
  const isMinHelseTjenester = erTjenester();

  dispatch({
    type: ActionTypes.SetIsAuthorizedAction,
    payload: { isAuthorized, isMinHelseTjenester },
  });
};

export const getInnloggetData = async (dispatch: Dispatch<DispatchAction>, errorText: string): Promise<void> => {
  try {
    const e = await getTjeneste<InnloggetDataResponse>('/api/v1/CrossDomain/InnloggetData');
    let payload: Partial<GlobalState> = {};

    const bruker: BrukerState = {
      visPersonvelger: !hasCookie('HN-ValgtRepresentasjon'),
      navn: e?.InnloggetData.RepresentertBruker,
      sistInnlogget: e?.InnloggetData.SistPaloggetDato,
      harRepresentasjon: e?.InnloggetData.HarRepresentasjon,
      representertBruker: e?.InnloggetData.RepresentertBruker,
      erMellom12Og16: e?.InnloggetData.ErMellom12Og16,
      scopes: e?.InnloggetData.Scopes,
      personverninnstillinger: e?.InnloggetData.AktiveUtvalgtePersonverninnstillinger,
    };
    payload = { bruker };

    payload = { ...payload, loading: { global: false } };

    dispatch({
      type: ActionTypes.GetUserAction,
      payload,
    });
  } catch (e) {
    const payload: Partial<GlobalState> = {
      error: { profileError: errorText },
      loading: { global: false },
    };
    dispatch({ type: ActionTypes.error, payload });
    logError('Feil ved henting av innlogget data', e);
  }
};

export const loggUt = (innbyggermenyLoggUtUrl: string): void => {
  const HelseNorgeUrlLogut = `${getTjenesterUrl()}${innbyggermenyLoggUtUrl}`;

  window.location.replace(HelseNorgeUrlLogut);
};

export const getAntallUlesteForAlle = (dispatch: Dispatch<DispatchAction>, errorText: string): void => {
  try {
    dispatch({ type: ActionTypes.SetLoadingAction, payload: { loading: { isAntallUlesteLoading: true } } });
    getProxy<HentAntallUlesteResponse>('Hendelsesmenyinternal', 'api/v2/AntallUleste')
      .then(r => {
        const payload: Partial<GlobalState> = {
          antallUlesteForAlleRepresentasjoner: r?.antallUlesteRepresentertGuids,
          loading: { isAntallUlesteLoading: false },
          error: { antallUlesteError: undefined },
        };
        dispatch({ type: ActionTypes.GetAntallUlesteForAlle, payload });
      })
      .catch((e: OperationResponse) => {
        // This catch is for asynchronous errors during the request
        dispatch({
          type: ActionTypes.error,
          payload: {
            loading: { isKunAntallUlesteLoading: false },
            error: { antallUlesteError: errorText },
          },
        });
        if (e.ErrorMessage && e.StatusCode !== 403) {
          logError(e.ErrorMessage.Title, e.ErrorMessage.Body);
        }
      });
  } catch (e) {
    // this catch is for any other error that could happen during runtime
    const payload: Partial<GlobalState> = {
      loading: { isKunAntallUlesteLoading: false },
      error: { antallUlesteError: errorText },
    };
    dispatch({ type: ActionTypes.error, payload });
    logError('Feil ved henting av antall uleste', e);
  }
};

export const getKunAntallUleste = (dispatch: Dispatch<DispatchAction>, errorText: string): void => {
  try {
    dispatch({ type: ActionTypes.SetLoadingAction, payload: { loading: { isKunAntallUlesteLoading: true } } });
    getProxy<HentKunAntallUlesteHendelserResponse>('Hendelsesmenyinternal', 'api/v2/Hendelse/KunAntallUleste')
      .then(r => {
        const payload: Partial<GlobalState> = {
          antallUlesteForValgtRepresentasjon: r?.antallUlesteHendelser,
          loading: { isKunAntallUlesteLoading: false },
          error: { kunAntallUlesteError: undefined },
        };
        dispatch({ type: ActionTypes.GetKunAntallUleste, payload });
      })
      .catch((e: OperationResponse) => {
        // This catch is for asynchronous errors during the request
        dispatch({
          type: ActionTypes.error,
          payload: {
            error: { kunAntallUlesteError: errorText },
            loading: { isKunAntallUlesteLoading: false },
          },
        });
        if (e.ErrorMessage && e.StatusCode !== 403) {
          logError(e.ErrorMessage.Title, e.ErrorMessage.Body);
        }
      });
  } catch (e) {
    // this catch is for any other error that could happen during runtime
    const payload: Partial<GlobalState> = {
      error: { kunAntallUlesteError: errorText },
      loading: { isKunAntallUlesteLoading: false },
    };
    dispatch({ type: ActionTypes.error, payload });
    logError('Feil ved henting av kun antall uleste', e);
  }
};

interface ResourceType {
  [key: string]: string | ResourceType;
}

/**
 * Remove domain from URL, in order to compare pathnames while ignoring differences in the domain name (eg. prod vs mas-01)
 */
const removeDomain = (value?: string | ResourceType): string | ResourceType | undefined => {
  if (typeof value === 'string' && value?.startsWith('https://')) {
    const url = new URL(value);

    return url.pathname;
  }

  return value;
};

const areEqual = (expected: ResourceType, compare: ResourceType, errors: Record<string, string> = {}): Record<string, string> => {
  Object.keys(expected).forEach(key => {
    if (typeof expected[key] === 'object' && key in compare) {
      areEqual(expected[key], compare[key] as ResourceType, errors);
    } else if (expected[key] && removeDomain(expected[key]) !== removeDomain(compare[key])) {
      errors[key] = `'${compare[key]}' != '${expected[key]}'`;
    }
  });

  return errors;
};

export const getTiles = (
  dispatch: Dispatch<DispatchAction>,
  endpoint: 'PublicTiles/v1' | 'AuthenticatedTiles/v1',
  errorText: string,
  languageCode: LanguageLocales
): void => {
  try {
    dispatch({
      type: ActionTypes.SetLoadingAction,
      payload: { loading: { isTilesLoading: true } },
    });
    get<GetPublicTilesResponse | GetAuthenticatedTilesResponse>('corefrontendinternal', endpoint, { culture: languageCode.toLowerCase() })
      .then(response => {
        const payload: Partial<GlobalState> = {
          tiles: response?.tiles,
          loading: { isTilesLoading: false },
          error: { tilesError: undefined },
        };
        dispatch({ type: ActionTypes.GetTilesAction, payload });
      })
      .catch(() => {
        // This catch is for asynchronous errors during the request
        dispatch({
          type: ActionTypes.error,
          payload: {
            tiles: undefined,
            error: { tilesError: errorText },
            loading: { isTilesLoading: false },
          },
        });
      });
  } catch (e) {
    // this catch is for any other error that could happen during runtime
    const payload: Partial<GlobalState> = {
      error: { tilesError: errorText },
      loading: { isTilesLoading: false },
    };
    dispatch({ type: ActionTypes.error, payload });
    logError('Feil ved henting av tiles fra proxy', e);
  }
};

export const getHeaderFooter = (dispatch: Dispatch<DispatchAction>, errorText: string, languageCode: LanguageLocales): void => {
  try {
    dispatch({
      type: ActionTypes.SetLoadingAction,
      payload: { loading: { isHeaderFooterLoading: true } },
    });
    ContentAPIGet<HeaderFooterDTO>('headerfooter', { languageCode })
      .then(headerFooter => {
        const headerFooterFallback = headerFooter ? merge(fallbackHeaderFooter, headerFooter) : fallbackHeaderFooter;
        const payload: GlobalState = {
          headerFooter: headerFooterFallback,
          loading: { isHeaderFooterLoading: false },
          error: { headerFooterError: undefined },
        };
        dispatch({ type: ActionTypes.GetHeaderFooterAction, payload });
        if (headerFooter?.footer) {
          HNeventSetFooterData({
            footerDTO: headerFooter.footer,
            language: languageCode,
          });
        }

        if (isDebug() && languageCode === LanguageLocales.NORWEGIAN) {
          const errors = areEqual(fallbackHeaderFooter as unknown as ResourceType, headerFooter as unknown as ResourceType);

          if (Object.keys(errors).length > 0) {
            warn('Feil ved sammenlikning av headerfooter-data fra API med fallback-data', errors);
          }
        }
      })
      .catch(() => {
        // This catch is for asynchronous errors during the request
        dispatch({
          type: ActionTypes.error,
          payload: {
            headerFooter: fallbackHeaderFooter,
            error: { headerFooterError: errorText },
            loading: { isHeaderFooterLoading: false },
          },
        });
      });
  } catch (e) {
    // this catch is for any other error that could happen during runtime
    const payload: Partial<GlobalState> = {
      error: { headerFooterError: errorText },
      loading: { isHeaderFooterLoading: false },
    };
    dispatch({ type: ActionTypes.error, payload });
    logError('Feil ved henting av headerfooter fra CMS', e);
  }
};

export const setIsListenerReady = (dispatch: Dispatch<DispatchAction>): void => {
  dispatch({ type: ActionTypes.SetIsListenerReady, payload: { islistenerReady: true } });
};
